import { type FC, type KeyboardEvent, useCallback, useState } from "react";
import { css } from "@stubhub/broadway-foundation/css";
import { Skeleton } from "@viagogo/core/ui";
import { Feature } from "@viagogo/feature";
import { FeatureSetEnum } from "src/shared/constants/FeatureSetEnum";
import { PE_KEYS } from "src/shared/constants/PageEventKeys";
import { PE_TYPE } from "src/shared/constants/PageEventTypes";
import { useStorePageEvent } from "src/shared/hooks/useStorePageEvent";
import { SIZE_TO_HEIGHT_MAP, SPOTIFY_PLAYER_SIZE } from "./constants";

const iframeBorderRadius = css({
  borderStartStartRadius: "element.default",
  borderStartEndRadius: "element.default",
  borderEndStartRadius: "element.default",
  borderEndEndRadius: "element.default",
});

const skeletonPlayerClass = css({
  width: "full",
  borderStartStartRadius: "element.default",
  borderStartEndRadius: "element.default",
  borderEndStartRadius: "element.default",
  borderEndEndRadius: "element.default",
  backgroundImage:
    "[linear-gradient(-60deg,var(--bway-colors-bg-surface-stronger),var(--bway-colors-bg-surface-stronger),var(--bway-colors-bg-surface-stronger),var(--bway-colors-bg-surface-strong),var(--bway-colors-bg-surface-stronger),var(--bway-colors-bg-surface-stronger),var(--bway-colors-bg-surface-strong))]",
  backgroundSize: "[400% 400%]",
  animationName: "[shimmer]",
  animationDuration: "[1.25s]",
  animationTimingFunction: "[linear]",
  animationIterationCount: "[infinite]",
});

interface Props {
  /** The height of the Spotify snippet (default 84) */
  height?: string;
  /** spotify track id from Spotify api */
  trackId: string;
  /** additional class name for styling */
  className?: string;
}

/**
 * Spotify snippet to play an artists track
 *
 * The iframe src would create sp_landing and sp_t cookie. they are part analytic part marketing
 * You can useCookiePreferences() to get user cookie preference to disable it if need to
 */
export const SpotifyEmbeddedPlayer: FC<Props> = ({ height = SPOTIFY_PLAYER_SIZE.SMALL, trackId, className }) => {
  const storePageEvent = useStorePageEvent();
  const [isLoading, setIsLoading] = useState(true);
  const recordClick = useCallback(() => {
    storePageEvent(PE_TYPE.CLICK, PE_KEYS.SPOTIFY_SNIPPET_CLICKED, trackId);
  }, [trackId, storePageEvent]);
  const handleLoad = () => {
    setIsLoading(false);
    storePageEvent(PE_TYPE.LOGGING, PE_KEYS.SPOTIFY_SNIPPET_LOADED, trackId);
  };
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === "Enter" || event.key === " ") {
        recordClick();
      }
    },
    [recordClick],
  );
  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions -- wrapper tracks analytics click on the entire spotify player area
    <div className={className} onClick={recordClick} onKeyDown={handleKeyDown}>
      {!!isLoading && (
        <Feature
          id={FeatureSetEnum.TMGlobalDSSkeleton}
          control={() => <div className={skeletonPlayerClass} style={{ height: `${SIZE_TO_HEIGHT_MAP[height]}px` }} />}
          variant={() => <Skeleton height={`${SIZE_TO_HEIGHT_MAP[height]}px`} />}
        />
      )}
      <iframe
        title="Spotify-Snippet"
        className={iframeBorderRadius}
        src={`https://open.spotify.com/embed/track/${trackId}?utm_source=generator&theme=0`}
        width={isLoading ? "0px" : "100%"}
        height={SIZE_TO_HEIGHT_MAP[height]}
        allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
        onLoad={handleLoad}
      />
    </div>
  );
};
